import React from 'react';
import { createBrowserRouter } from 'react-router';
import { App } from '../../App';
import { PermissionType } from '../../enums/permission-type';
import { CompaniesPage } from '../../pages/companies/companies.page';
import { ErrorPage } from '../../pages/error/error.page';
import { ProfilePage } from '../../pages/profile/profile.page';
import { VersionPage } from '../../pages/version/version.page';
import { YourSkillsPage } from '../../pages/your-skills/your-skills.page';
import { RouterGuard } from '../guards/router.guard';
import { PagePath, Mode } from '../navigation.enums';
import { AccountRouter } from './account.router';
import { UserManagementRouter } from './user-management.router';
import { AnalysisRouter } from './analysis.router';
import { AnalyticsRouter } from './analytics.router';
import { LearningJourneyRouter } from './learning-journey.router';
import { LearningManagementRouter } from './learning-management.router';
import { RoleSkillManagementRouter } from './role-skill-management.router';
import { ModeRedirect, ErrorRedirect } from './routers.helpers';
import { YourAssessmentsRouter } from './your-assessments.router';
import { YourCareerRouter } from './your-career.router';
import { AssessmentManagementRouter } from './assessment-management.router';
import { ReportManagementPage } from '../../pages/report-management/report-management.page';
import { AIManagementRouter } from './ai-management.router';
import { FeatureFlagType } from '../../enums/feature-flag-type';
import { FreemiumUserManagementRouter } from './freemium-user-management.router';
import { FreemiumCreateAssessmentPage } from '../../pages/freemium-feature/freemium-placeholders/freemium-create-assessment.page';
import { OnboardingPage } from '../../pages/onboarding/onboarding.page';
import { DashboardPage } from '../../pages/dashboard/dashboard.page';

export const router = createBrowserRouter([
  {
    path: '/',
    element: <App />,
    errorElement: <ErrorPage />,
    children: [
      // routers
      {
        path: PagePath.accountConfig,
        element: (
          <RouterGuard authStatusRequired="Unauthenticated">
            <AccountRouter />
          </RouterGuard>
        ),
      },
      {
        path: PagePath.onboarding,
        element: (
          <RouterGuard featureFlagsRequired={[FeatureFlagType.ClassicFeatures, FeatureFlagType.FreemiumSignup]} authStatusRequired="Authenticated" onboardingStatusRequired="Incomplete">
            <OnboardingPage />
          </RouterGuard>
        ),
      },
      {
        path: PagePath.analysisConfig,
        element: (
          <RouterGuard authStatusRequired="Authenticated" featureFlagsRequired={[FeatureFlagType.ClassicFeatures, FeatureFlagType.FreemiumSignup]} onboardingStatusRequired="Complete" skillAssessmentStatusRequired="Incomplete">
            <AnalysisRouter />
          </RouterGuard>
        ),
      },
      {
        path: PagePath.learningJourneyConfig,
        element: (
          <RouterGuard authStatusRequired="Authenticated" featureFlagsRequired={[FeatureFlagType.ClassicFeatures]} onboardingStatusRequired="Complete" skillAssessmentStatusRequired="Complete" modeRequired={Mode.UserDashboard}>
            <LearningJourneyRouter />
          </RouterGuard>
        ),
      },
      {
        path: PagePath.yourAssessmentsConfig,
        element: (
          <RouterGuard authStatusRequired="Authenticated" featureFlagsRequired={[FeatureFlagType.ClassicFeatures, FeatureFlagType.FreemiumSignup]} onboardingStatusRequired="Complete" skillAssessmentStatusRequired="Complete" modeRequired={Mode.UserDashboard}>
            <YourAssessmentsRouter />
          </RouterGuard>
        ),
      },
      {
        path: PagePath.yourCareerConfig,
        element: (
          <RouterGuard authStatusRequired="Authenticated" featureFlagsRequired={[FeatureFlagType.ClassicFeatures]} onboardingStatusRequired="Complete" skillAssessmentStatusRequired="Complete" modeRequired={Mode.UserDashboard}>
            <YourCareerRouter />
          </RouterGuard>
        ),
      },
      {
        path: PagePath.userManagementConfig,
        element: (
          <RouterGuard authStatusRequired="Authenticated" featureFlagsRequired={[FeatureFlagType.ClassicFeatures]} onboardingStatusRequired="Complete" skillAssessmentStatusRequired="Complete" permissionTypeRequired={PermissionType.Administration}>
            <UserManagementRouter />
          </RouterGuard>
        ),
      },
      {
        path: PagePath.freemiumUserManagementConfig,
        element: (
          <RouterGuard authStatusRequired="Authenticated" featureFlagsRequired={[FeatureFlagType.FreemiumSignup]} onboardingStatusRequired="Complete" skillAssessmentStatusRequired="Complete">
            <FreemiumUserManagementRouter />
          </RouterGuard>
        ),
      },
      {
        path: PagePath.freemiumAssessment,
        element: (
          <RouterGuard authStatusRequired="Authenticated" featureFlagsRequired={[FeatureFlagType.FreemiumSignup]} onboardingStatusRequired="Complete" skillAssessmentStatusRequired="Complete">
            <FreemiumCreateAssessmentPage />
          </RouterGuard>
        ),
      },
      {
        path: PagePath.analyticsConfig,
        element: (
          <RouterGuard authStatusRequired="Authenticated" featureFlagsRequired={[FeatureFlagType.ClassicFeatures, FeatureFlagType.FreemiumSignup]} onboardingStatusRequired="Complete" skillAssessmentStatusRequired="Complete" permissionTypeRequired={PermissionType.Analytics}>
            <AnalyticsRouter />
          </RouterGuard>
        ),
      },
      {
        path: PagePath.assessmentManagementConfig,
        element: (
          <RouterGuard authStatusRequired="Authenticated" featureFlagsRequired={[FeatureFlagType.ClassicFeatures]} onboardingStatusRequired="Complete" skillAssessmentStatusRequired="Complete" permissionTypeRequired={PermissionType.Assessments}>
            <AssessmentManagementRouter />
          </RouterGuard>
        ),
      },
      {
        path: PagePath.learningManagementConfig,
        element: (
          <RouterGuard authStatusRequired="Authenticated" featureFlagsRequired={[FeatureFlagType.ClassicFeatures]} onboardingStatusRequired="Complete" skillAssessmentStatusRequired="Complete" permissionTypeRequired={PermissionType.LearningManagement}>
            <LearningManagementRouter />
          </RouterGuard>
        ),
      },
      {
        path: PagePath.reportManagement,
        element: (
          <RouterGuard authStatusRequired="Authenticated" featureFlagsRequired={[FeatureFlagType.ClassicFeatures]} onboardingStatusRequired="Complete" skillAssessmentStatusRequired="Complete" modeRequired={Mode.AdminDashboard}>
            <ReportManagementPage />
          </RouterGuard>
        ),
      },
      {
        path: PagePath.roleSkillManagementConfig,
        element: (
          <RouterGuard authStatusRequired="Authenticated" featureFlagsRequired={[FeatureFlagType.ClassicFeatures, FeatureFlagType.FreemiumSignup]} onboardingStatusRequired="Complete" skillAssessmentStatusRequired="Complete" permissionTypeRequired={PermissionType.RoleSkillManagement}>
            <RoleSkillManagementRouter />
          </RouterGuard>
        ),
      },
      {
        path: PagePath.aiManagementConfig,
        element: (
          <RouterGuard authStatusRequired="Authenticated" featureFlagsRequired={[FeatureFlagType.ClassicFeatures]} onboardingStatusRequired="Complete" skillAssessmentStatusRequired="Complete" modeRequired={Mode.AdminDashboard}>
            <AIManagementRouter />
          </RouterGuard>
        ),
      },
      // pages
      {
        path: PagePath.profile,
        element: (
          <RouterGuard authStatusRequired="Authenticated" featureFlagsRequired={[FeatureFlagType.ClassicFeatures, FeatureFlagType.FreemiumSignup]} onboardingStatusRequired="Complete" skillAssessmentStatusRequired="Complete">
            <ProfilePage />
          </RouterGuard>
        ),
      },
      {
        path: PagePath.version,
        element: (
          <RouterGuard authStatusRequired="Authenticated" featureFlagsRequired={[FeatureFlagType.ClassicFeatures]} onboardingStatusRequired="Complete" skillAssessmentStatusRequired="Complete">
            <VersionPage />
          </RouterGuard>
        ),
      },
      {
        path: PagePath.companies,
        element: (
          <RouterGuard authStatusRequired="Authenticated" featureFlagsRequired={[FeatureFlagType.ClassicFeatures]} onboardingStatusRequired="Complete" skillAssessmentStatusRequired="Complete" modeRequired={Mode.AdminDashboard}>
            <CompaniesPage />
          </RouterGuard>
        ),
      },
      {
        path: PagePath.dashboard,
        element: (
          <RouterGuard authStatusRequired="Authenticated" onboardingStatusRequired="Complete" skillAssessmentStatusRequired="Complete" modeRequired={Mode.UserDashboard}>
            <DashboardPage />
          </RouterGuard>
        ),
      },
      {
        path: PagePath.yourSkills,
        element: (
          <RouterGuard authStatusRequired="Authenticated" featureFlagsRequired={[FeatureFlagType.ClassicFeatures, FeatureFlagType.FreemiumSignup]} onboardingStatusRequired="Complete" skillAssessmentStatusRequired="Complete" modeRequired={Mode.UserDashboard}>
            <YourSkillsPage />
          </RouterGuard>
        ),
      },

      // other
      { path: PagePath.error, element: <ErrorPage /> },
      {
        path: PagePath.root,
        element: (
          <RouterGuard authStatusRequired="Authenticated">
            <ModeRedirect />
          </RouterGuard>
        ),
      },
      { path: PagePath.rootWildcard, element: <ErrorRedirect /> },
    ],
  },
]);
